import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  ArrowRight,
  BookOpen,
  GraduationCap,
  Download,
  Megaphone,
  Send,
  Sparkles,
  ShieldCheck,
  Languages,
} from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const MODULES = [
  {
    icon: BookOpen,
    title: "Knowledge Base",
    desc: "Every answer your leaders need — vetted, categorised, always current.",
  },
  {
    icon: GraduationCap,
    title: "Academy",
    desc: "Curated trainings and video lessons to sharpen leadership and business skills.",
  },
  {
    icon: Download,
    title: "Downloads",
    desc: "One home for every asset — slides, PDFs, scripts, marketing kits.",
  },
  {
    icon: Megaphone,
    title: "Announcements",
    desc: "Company-wide updates so nobody misses what matters.",
  },
  {
    icon: Send,
    title: "Telegram Channels",
    desc: "Direct links to every official EM channel and community.",
  },
  {
    icon: Sparkles,
    title: "EM AI",
    desc: "Your personal assistant. Answers grounded in the platform's Knowledge Base.",
  },
];

const HIGHLIGHTS = [
  {
    icon: Sparkles,
    title: "AI-first",
    desc: "Retrieval-augmented assistant that only answers from verified EM content — never guesses.",
  },
  {
    icon: Languages,
    title: "Multilingual",
    desc: "Full UI and content in Dutch, English and Spanish — with automatic on-demand translation.",
  },
  {
    icon: ShieldCheck,
    title: "Leader-grade access",
    desc: "Invitation-only, role-based. Every leader gets exactly what they need — nothing more.",
  },
];

export default function OperationSystem() {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-ink-0 text-white relative z-10 overflow-x-hidden">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-ink-0/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 lg:px-10 h-16">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-xl font-semibold tracking-tight">EM</span>
            <span className="font-display text-xl font-light tracking-tight text-gold">Operating System</span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link
              to="/login"
              data-testid="opsys-signin"
              className="inline-flex items-center gap-2 bg-gold text-ink-0 font-semibold rounded-lg px-4 py-2 text-sm hover:bg-gold-hover group"
              style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
            >
              Sign in
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5" style={{ transitionProperty: "transform", transitionDuration: "180ms" }} />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative border-b border-white/5 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1778849122862-7fa43a7200f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBtaW5pbWFsJTIwdGV4dHVyZSUyMHByZW1pdW18ZW58MHx8fHwxNzg0NTU0NDkwfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none"
        />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(10,10,11,0.6) 0%, rgba(10,10,11,0.95) 100%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-24 lg:py-36">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold mb-6">Internal Operating System</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.05] max-w-4xl">
            One place for every answer,<br />
            every asset, <span className="text-gold">every leader</span>.
          </h1>
          <p className="mt-8 text-lg text-zinc-400 max-w-2xl leading-relaxed">
            Knowledge, training, downloads and announcements — engineered for a fast-growing affiliate network.
            Built for EM leaders, powered by AI, secured by invitation.
          </p>
          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <Link
              to="/signup"
              data-testid="opsys-hero-signup"
              className="inline-flex items-center gap-2 bg-gold text-ink-0 font-semibold rounded-lg px-6 py-3 hover:bg-gold-hover group"
              style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
            >
              Get access — 150 USDT
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5" style={{ transitionProperty: "transform", transitionDuration: "180ms" }} />
            </Link>
            <Link
              to="/login"
              data-testid="opsys-hero-signin"
              className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white px-4 py-3"
              style={{ transitionProperty: "color", transitionDuration: "180ms" }}
            >
              Already have access? Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* Modules grid */}
      <section id="modules" className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold mb-4">What&apos;s inside</p>
          <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight mb-4">
            Everything a leader needs, in one place.
          </h2>
          <p className="text-zinc-400 text-base max-w-2xl leading-relaxed mb-14">
            EM OS unifies knowledge, training, assets and AI into a single premium workspace &mdash; accessible from anywhere, in your own language.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MODULES.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                data-testid={`opsys-module-${title.toLowerCase().replace(/\s/g, "-")}`}
                className="em-card p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-gold/10 border border-gold/25 flex items-center justify-center mb-5">
                  <Icon className="w-4.5 h-4.5 text-gold" strokeWidth={1.75} />
                </div>
                <h3 className="font-display text-lg text-white mb-2">{title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="border-b border-white/5 bg-white/[0.015]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {HIGHLIGHTS.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4">
                <div className="w-9 h-9 rounded-lg bg-gold/10 border border-gold/25 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-gold" strokeWidth={1.75} />
                </div>
                <div>
                  <p className="font-display text-base text-white mb-1.5">{title}</p>
                  <p className="text-sm text-zinc-500 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Access CTA */}
      <section>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="em-card p-10 lg:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.08) 0%, transparent 55%)" }} />
            <div className="relative">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold mb-4">Get started</p>
              <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight mb-4 max-w-2xl mx-auto">
                Ready to step in?
              </h2>
              <p className="text-zinc-400 text-base max-w-xl mx-auto leading-relaxed mb-10">
                One-time <span className="text-white font-semibold">150 USDT</span> activates full access for a year. Pay from your own wallet on the Ethereum (ERC-20) network — no third parties, no subscriptions.
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Link
                  to="/signup"
                  data-testid="opsys-cta-signup"
                  className="inline-flex items-center gap-2 bg-gold text-ink-0 font-semibold rounded-lg px-6 py-3 hover:bg-gold-hover group"
                  style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
                >
                  Create your account
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5" style={{ transitionProperty: "transform", transitionDuration: "180ms" }} />
                </Link>
                <Link
                  to="/login"
                  data-testid="opsys-cta-signin"
                  className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white px-4 py-3"
                  style={{ transitionProperty: "color", transitionDuration: "180ms" }}
                >
                  Already have access? Sign in
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-baseline gap-2 text-sm">
            <span className="font-display font-semibold">EM</span>
            <span className="font-display font-light text-gold">Operating System</span>
          </div>
          <p className="text-xs text-zinc-600">Knowledge · Strategy · Results</p>
        </div>
      </footer>
    </div>
  );
}
